import type {Element, ElementContent} from 'hast'
import type {Schema} from 'hast-util-sanitize'
import {h} from 'hastscript'
import type {Delimiter, DelimiterParents, DelimiterTokenType, Nodes as MarkdownNodes} from 'mdast'
import {type Handler, type Options, toHast} from 'mdast-util-to-hast'

import {setOfAll} from '../../../utils'
import {compareNodePositions, isPositionedNode} from '../../syntax-tree/positioned-node'

export const delimiterProperty = 'dataDelimiter'

function getDelimiterChildren(node: MarkdownNodes): Delimiter[] {
  // For some reason a simple `filter` call with a type guard isn't satisfying the typechecker here, but this is
  const result: Delimiter[] = []
  for (const child of node.children ?? []) if (child.type === 'delimiter') result.push(child)
  return result
}

const delimiterTags: Partial<Record<DelimiterTokenType, string>> = {
  tableDelimiter: 'td',
  tableDelimiterRow: 'tr',
}

function createDelimiterElement(delimiter: Delimiter): Element {
  const properties = {[delimiterProperty]: delimiter.tokenType}
  const children = delimiter.children?.length ? delimiter.children.map(createDelimiterElement) : delimiter.raw

  const node = h(delimiterTags[delimiter.tokenType] ?? 'span', properties, children)

  if (isPositionedNode(delimiter))
    node.position = {start: {...delimiter.position.start}, end: {...delimiter.position.end}}

  return node
}

/**
 * Nodes that aren't normally expected to have children won't get their children processed by hast, even if we have a
 * handler for them. So we have to implement a custom handler that preserves the delimiter children ourselves.
 */
const handleDelimiterParentNode: Handler = (state, node: MarkdownNodes) => {
  const result = toHast(node) as ElementContent
  if (result.type === 'element') {
    const delimiterChildren = getDelimiterChildren(node).map(createDelimiterElement)
    if (result.children?.length) {
      result.children = [...result.children, ...delimiterChildren].sort(compareNodePositions)
    } else {
      // If there's no existing children, just set the already-sorted delimiters as the children
      result.children = delimiterChildren
    }
  } else if (node.type === 'text') {
    // Text nodes can't contain children, so we make the delimiters a sibling. This is technically resulting in an
    // incorrect tree because the text position spans over its siblings' positions, but that's not an issue because we
    // don't actually use `text` nodes anywhere.
    return [result, ...getDelimiterChildren(node).map(createDelimiterElement)]
  }
  return result
}

type DelimiterParentType = Exclude<
  DelimiterParents['type'],
  // We actually do want to handle displaymath and inlinemath eventually, but we don't currently support math at all so those
  // node types don't exist unless we add the markdown-math extension, which we aren't ready to do yet. The problem is
  // that the types will still _sometimes_ exist, if consumer depends on both markdown-math and hybrid-markdown-editor,
  // so we have to exclude them for now.
  'delimiter' | 'html' | 'raw' | 'displaymath' | 'inlinemath'
>

const delimiterParentTypes = setOfAll<DelimiterParentType>()(
  'break',
  'code',
  'definition',
  'footnoteReference',
  'image',
  'imageReference',
  'inlineCode',
  'text',
  'thematicBreak',
  'yaml',
)

const delimiterParentHandlers = Object.fromEntries(
  Array.from(delimiterParentTypes).map(t => [t, handleDelimiterParentNode]),
)

const handleDelimiter: Handler = (state, delimiter: Delimiter) => {
  return createDelimiterElement(delimiter)
}

export function getDelimiterType(element: Element): DelimiterTokenType | null {
  const prop = element.properties?.[delimiterProperty]
  return typeof prop === 'string' ? (prop as DelimiterTokenType) : null
}

/** Adds support for passing delimiter nodes through from the mdast tree. */
export const hastDelimiters: Options = {
  handlers: {...delimiterParentHandlers, delimiter: handleDelimiter},
}

export const delimitersSanitizeSchema: Schema = {
  attributes: {
    ['*']: [delimiterProperty],
  },
}
